import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import ManagedUserStatusIcon from './ManagedUserStatusIcon';

const groupStyles = {
  admin: { label: 'Admin', icon: 'admin' },
  'projects-admin': { label: 'Project Admin', icon: 'projectsAdmin' },
  'project-user': { label: 'Project User', icon: 'projectUser' },
  'podman-user': { label: 'Podman User', icon: 'podmanUser' }
};
export const groupPresentation = (name) => Object.prototype.hasOwnProperty.call(groupStyles, name)
  ? groupStyles[name] : { label: name, icon: 'group' };

export const confirmUserAction = (action, selectedUserCount) => {
  if (action.action !== 'delete') return true;
  return window.confirm(`Are you sure you want to permanently delete ${selectedUserCount} selected user${selectedUserCount === 1 ? '' : 's'}?`);
};

export const groupAction = (name, enabled) => ({
  action: `${enabled ? 'add-group' : 'remove-group'}:${name}`,
  patch: { [enabled ? 'addGroups' : 'removeGroups']: [name] }
});

export const roleStates = (selectedUsers, groups = []) => [...new Map(groups.map((group) => [group.name, group])).values()]
  .sort((left, right) => left.name.localeCompare(right.name))
  .map((group) => {
    const count = selectedUsers.filter((user) => (user.groups || []).includes(group.name)).length;
    return { ...group, count, state: count === 0 ? 'unassigned' : count === selectedUsers.length ? 'assigned' : 'mixed' };
  });

export const editableRoles = (users, groups) => roleStates(users, groups)
  .filter((role) => users.length === 1 || role.state === 'unassigned');

export const userActions = (selectedUsers) => {
  if (!selectedUsers.length) return [];
  const bulk = selectedUsers.length > 1;
  const accountAction = (enabled) => ({
    action: enabled ? 'enable' : 'disable', label: `${enabled ? 'Enable' : 'Disable'} ${bulk ? 'Selected Users' : 'User'}`,
    description: enabled ? 'enable' : 'disable', patch: { enabled }
  });
  return [
    { action: 'modify-roles', label: 'Modify roles…' },
    ...(bulk ? [accountAction(true), accountAction(false)] : [accountAction(!selectedUsers[0].enabled)]),
    { action: 'delete', label: bulk ? 'Delete Selected Users' : 'Delete User', description: 'permanently delete' }
  ];
};

export function UserRolesDialog({ users, groups, disabled, error, onAction, onClose }) {
  const dialogRef = useRef(null);
  const pending = useRef(false);
  const [savingGroup, setSavingGroup] = useState(null);
  const [localError, setLocalError] = useState('');
  const busy = disabled || savingGroup !== null;
  const roles = editableRoles(users, groups);
  useEffect(() => {
    // A bulk assignment removes its row. Keep keyboard focus inside the dialog
    // when a refreshed list removes the focused control or saving disables it.
    if (!dialogRef.current.contains(document.activeElement) || document.activeElement.disabled) {
      dialogRef.current.focus();
    }
  }, [users, groups, busy]);
  const close = () => { if (!busy) onClose(); };
  const toggleRole = async (role, assigned) => {
    if (busy || pending.current) return;
    pending.current = true;
    setSavingGroup(role.name);
    setLocalError('');
    try {
      await onAction(groupAction(role.name, assigned));
    } catch (actionError) {
      setLocalError(actionError.message || 'Could not update roles.');
    } finally {
      pending.current = false;
      setSavingGroup(null);
    }
  };
  const handleKeyDown = (event) => {
    if (event.key === 'Escape') { event.stopPropagation(); close(); }
    if (event.key !== 'Tab') return;
    const controls = [...dialogRef.current.querySelectorAll('button:not(:disabled)')];
    if (!controls.length) { event.preventDefault(); return; }
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current)) {
      event.preventDefault(); last.focus();
    } else if (!event.shiftKey && (document.activeElement === last || document.activeElement === dialogRef.current)) {
      event.preventDefault(); first.focus();
    }
  };
  return createPortal(<div className="node-modal-backdrop user-roles-backdrop" onMouseDown={(event) => {
    event.stopPropagation();
    if (event.target === event.currentTarget) close();
  }}>
    <section ref={dialogRef} className="node-modal user-roles-modal" tabIndex={-1} role="dialog" aria-modal="true"
      aria-labelledby="user-roles-title" aria-describedby="user-roles-help" aria-busy={busy} onKeyDown={handleKeyDown}>
      <button type="button" className="node-modal-close" aria-label="Close role editor" disabled={busy} onClick={close}>×</button>
      <span className="eyebrow">User management</span>
      <h2 id="user-roles-title">Modify roles</h2>
      <div className="user-roles-selection">
        <strong>{users.length === 1 ? (users[0].email || users[0].username) : `${users.length} selected users`}</strong>
        {users.length > 1 && <span>{users.map((user) => user.email || user.username).join(', ')}</span>}
      </div>
      <p id="user-roles-help">{users.length > 1
        ? 'Only roles that none of the selected users already has are shown. Click to assign a role to everyone. Changes save immediately.'
        : 'Click a role to assign or remove it. Changes save immediately.'}</p>
      <div className="user-roles-legend" aria-label="Role status legend">
        <span><i className="role-indicator assigned" />Assigned</span>
        <span><i className="role-indicator unassigned" />Unassigned</span>
      </div>
      <div className="user-role-list">
        {roles.map((role) => {
          const presentation = groupPresentation(role.name);
          const assigned = role.state === 'assigned';
          const status = assigned ? 'Assigned' : 'Unassigned';
          return <div className="user-role-entry" key={role.name}>
            <button type="button" className={`user-role-toggle ${role.state}`} disabled={busy}
              aria-pressed={assigned}
              aria-label={`${presentation.label}: ${status}. ${assigned ? 'Remove from' : 'Assign to'} ${users.length === 1 ? 'user' : 'all selected users'}`}
              onClick={() => toggleRole(role, !assigned)}>
              <ManagedUserStatusIcon type={presentation.icon} label={presentation.label} />
              <span className="user-role-copy"><strong>{presentation.label}</strong><small>{role.name}</small>{role.description && <small>{role.description}</small>}</span>
              <span className="user-role-status"><i className={`role-indicator ${role.state}`} />{savingGroup === role.name ? 'Saving…' : status}</span>
            </button>
          </div>;
        })}
        {!groups.length && <p className="settings-empty-state">No roles are available in this user pool.</p>}
        {groups.length > 0 && !roles.length && <p className="settings-empty-state">No roles can be assigned to every selected user. Each role is already assigned to at least one of them. Select a single user to edit existing memberships.</p>}
      </div>
      {(localError || error) && <div className="node-modal-error" role="alert">{localError || error}</div>}
      <div className="user-roles-footer">
        <span role="status">{busy ? 'Saving changes…' : 'Showing current memberships'}</span>
        <button type="button" onClick={close} disabled={busy}>Done</button>
      </div>
    </section>
  </div>, document.body);
}

export default function UserActionsSelect({ users, groups = [], selectedUsernames, disabled, error, onAction }) {
  const [rolesOpen, setRolesOpen] = useState(false);
  const selectRef = useRef(null);
  const selectedUsers = users.filter((user) => selectedUsernames.includes(user.username));
  const options = userActions(selectedUsers);
  useEffect(() => {
    if (!selectedUsernames.length) setRolesOpen(false);
  }, [selectedUsernames.length]);
  // Recreate the native select when its menu changes, so browsers don't retain
  // stale options across single/bulk selection or membership updates.
  const menuKey = JSON.stringify(options);
  return <><select
    ref={selectRef}
    key={menuKey}
    value=""
    disabled={!selectedUsers.length || disabled}
    onChange={(event) => {
      const action = event.currentTarget.value;
      event.currentTarget.value = '';
      const option = options.find((option) => option.action === action);
      if (option?.action === 'modify-roles') setRolesOpen(true);
      else if (option) onAction(option);
    }}
    aria-label="User actions"
  >
    <option value="">User actions</option>
    {options.map((option) => <option key={option.action} value={option.action}>{option.label}</option>)}
  </select>
    {rolesOpen && selectedUsers.length > 0 && <UserRolesDialog users={selectedUsers} groups={groups}
      disabled={disabled} error={error} onAction={onAction} onClose={() => { setRolesOpen(false); selectRef.current?.focus(); }} />}
  </>;
}
